import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'

const CHART_COLOURS = [
  'var(--cat-1)',
  'var(--cat-2)',
  'var(--cat-3)',
  'var(--cat-4)',
  'var(--cat-5)',
  'var(--cat-6)',
  'var(--cat-7)',
  'var(--cat-8)',
]

const SECTION_DISPLAY: Record<string, string> = {
  threat_landscape_ir: 'Threat / IR',
  vulnerabilities_exploitation: 'Vulns',
  vendor_intelligence: 'Vendor Intel',
  governance_risk_compliance: 'GRC',
  cloud_saas_identity: 'Cloud / Identity',
  supply_chain_third_party: 'Supply Chain',
  industry_news: 'Industry News',
  security_awareness: 'Awareness',
}

export interface AttentionMixProps {
  data: Array<{ date: string; counts: Record<string, number> }>
  windowLabel: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

export function AttentionMix({ data, windowLabel, isLoading, isError, onRetry }: AttentionMixProps) {
  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Threat landscape mix</h3>
        <Skeleton className="h-[200px] w-full" />
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Threat landscape mix</h3>
        <ErrorState title="Couldn't load threat landscape data." onRetry={onRetry} />
      </Card>
    )
  }

  if (data.length === 0) {
    return (
      <Card data-testid="analytics-attention-mix" className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Threat landscape mix</h3>
        <p className="text-sm text-muted-foreground">No threat landscape data in the selected {windowLabel} window.</p>
      </Card>
    )
  }

  const sections = Array.from(new Set(data.flatMap((d) => Object.keys(d.counts))))

  const chartData = data.map((d) => ({
    date: d.date.slice(5),
    ...Object.fromEntries(sections.map((s) => [s, d.counts[s] ?? 0])),
  }))

  return (
    <Card data-testid="analytics-attention-mix" className="p-4">
      <h3 className="mb-1 text-sm font-semibold">Threat landscape mix</h3>
      <p className="mb-3 text-xs text-muted-foreground">
        Selected {windowLabel} window · directional share of stories by threat area, not incident volume.
      </p>
      <div className="h-[200px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            stackOffset="expand"
            margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--chart-grid-stroke)"
            />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: 'var(--chart-axis-label)' }}
              stroke="var(--chart-axis-stroke)"
            />
            <YAxis
              tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
              tick={{ fontSize: 10, fill: 'var(--chart-axis-label)' }}
              stroke="var(--chart-axis-stroke)"
              width={36}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--surface-elevated)',
                border: '1px solid var(--border-default)',
                borderRadius: 6,
                fontSize: 11,
                color: 'var(--text-primary)',
              }}
              formatter={(v: number, key: string) => [
                `${Math.round(v * 100)}%`,
                SECTION_DISPLAY[key] ?? key,
              ]}
            />
            <Legend
              iconSize={10}
              wrapperStyle={{ fontSize: 10, color: 'var(--text-primary)' }}
              formatter={(key: string) => SECTION_DISPLAY[key] ?? key}
            />
            {sections.map((s, i) => (
              <Area
                key={s}
                type="monotone"
                dataKey={s}
                stackId="1"
                stroke={CHART_COLOURS[i % CHART_COLOURS.length]}
                fill={CHART_COLOURS[i % CHART_COLOURS.length]}
                fillOpacity={0.7}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}
